import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../services/supabaseClient';

// ============ Styled ============
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 28px;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 36px;
    font-weight: 500;
    color: ${p => p.theme.ink};
    margin: 0 0 4px;
    letter-spacing: -0.3px;
  }
  p { color: ${p => p.theme.muted}; font-size: 13px; }
`;

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 14px 18px;
  input {
    flex: 1;
    min-width: 200px;
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px;
    padding: 9px 12px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
`;
const Filter = styled.button`
  border: 1px solid ${p => p.$active ? p.theme.gold : p.theme.line};
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  letter-spacing: 0.3px;
  &:hover { border-color: ${p => p.theme.gold}; }
`;

const ListBox = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  align-items: center;
  padding: 14px 22px;
  border-bottom: 1px solid ${p => p.theme.line};
  background: ${p => p.$problem ? 'rgba(154,59,59,.04)' : 'transparent'};
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.surfaceAlt}; }
  .name {
    font-size: 14px;
    color: ${p => p.theme.ink};
    font-family: ${p => p.$mono ? "'SF Mono', monospace" : 'inherit'};
    display: flex; align-items: center; gap: 10px;
  }
  .problem-flag {
    font-size: 9px;
    letter-spacing: 1px;
    text-transform: uppercase;
    background: rgba(154,59,59,.15);
    color: #C5544A;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 600;
  }
  .count {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: ${p => p.theme.gold};
    text-align: right;
  }
  .actions {
    display: flex;
    gap: 6px;
  }
  .actions button {
    border: 1px solid ${p => p.theme.line};
    background: transparent;
    color: ${p => p.theme.inkSoft};
    padding: 6px 10px;
    border-radius: 5px;
    font-size: 11px;
    cursor: pointer;
    font-family: inherit;
    letter-spacing: 0.3px;
    transition: all .15s;
    &:hover { color: ${p => p.theme.gold}; border-color: ${p => p.theme.gold}; }
    &[data-danger]:hover { color: ${p => p.theme.danger}; border-color: ${p => p.theme.danger}; }
    &[data-primary] {
      background: ${p => p.theme.gold};
      color: #fff;
      border-color: ${p => p.theme.gold};
    }
  }
`;
const RenameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 10px;
  align-items: center;
  padding: 14px 22px;
  background: ${p => p.theme.goldSoft};
  border-bottom: 1px solid ${p => p.theme.line};
  input {
    background: ${p => p.theme.surface};
    border: 1px solid ${p => p.theme.gold};
    color: ${p => p.theme.ink};
    border-radius: 7px;
    padding: 9px 12px;
    font-size: 14px;
    font-family: inherit;
    outline: none;
  }
  .help { font-size: 11px; color: ${p => p.theme.muted}; grid-column: 1 / -1; margin-top: 2px; }
  button {
    border: 1px solid ${p => p.theme.line};
    background: ${p => p.theme.surface};
    color: ${p => p.theme.ink};
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;
    &[data-primary] {
      background: ${p => p.theme.ink};
      color: ${p => p.theme.bg};
      border-color: ${p => p.theme.ink};
    }
  }
`;

const Empty = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${p => p.theme.muted};
  font-size: 13px;
`;

const Msg = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 14px;
  background: ${p => p.$ok ? 'rgba(47,125,91,.1)' : p.$err ? 'rgba(154,59,59,.1)' : p.theme.surfaceAlt};
  color: ${p => p.$ok ? p.theme.ok : p.$err ? p.theme.danger : p.theme.ink};
  border: 1px solid ${p => p.$ok ? p.theme.ok : p.$err ? p.theme.danger : p.theme.line};
`;

// ============ Helpers ============
// detecta amenidades "problemáticas" (snake_case, tudo lowercase, etc.)
function isProblematic(name) {
  if (!name) return false;
  if (/_/.test(name)) return true; // snake_case
  if (name === name.toLowerCase() && name.length > 3 && !/^[a-z][a-zé\s]+$/.test(name)) return true;
  if (/^[a-z0-9_-]+$/.test(name) && !/\s/.test(name)) return true; // só lowercase sem espaço
  return false;
}

// Stopwords pt-BR que ficam minúsculas no meio da frase
const STOPWORDS = new Set([
  'de', 'da', 'do', 'das', 'dos',
  'e', 'em', 'a', 'o', 'as', 'os',
  'à', 'às', 'aos',
  'na', 'no', 'nas', 'nos',
  'com', 'sem', 'para', 'por',
]);

// Capitalização inteligente que respeita acentos e abreviações
function titleCasePT(str) {
  return String(str || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word, i) => {
      // Preserva abreviações em CAIXA ALTA (CFTV, 24h misturado etc.)
      if (word.length > 1 && word === word.toUpperCase() && /[A-Z]/.test(word)) {
        return word;
      }
      const lower = word.toLowerCase();
      if (i > 0 && STOPWORDS.has(lower)) return lower;
      // Primeira letra upper (Unicode-aware) + resto lower
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

// sugestão de normalização
function suggestNormalized(name) {
  if (!name) return '';
  let s = name
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  s = titleCasePT(s);
  // pequenas correções de acentuação comum (apenas formas sem acento → com acento)
  return s
    .replace(/\bSeguranca\b/g, 'Segurança')
    .replace(/\bArea\b/g, 'Área')
    .replace(/\bAgua\b/g, 'Água')
    .replace(/\bAreas\b/g, 'Áreas')
    .replace(/\bSalao\b/g, 'Salão')
    .replace(/\bVarandao\b/g, 'Varandão')
    .replace(/\bMobilia\b/g, 'Mobília')
    .replace(/\bCameras\b/g, 'Câmeras')
    .replace(/\bEletrica\b/gi, 'Elétrica')
    .replace(/\bEletrico\b/gi, 'Elétrico')
    .replace(/\bVigilancia\b/g, 'Vigilância')
    .replace(/\bBiometrico\b/g, 'Biométrico')
    .replace(/\bAcademia\b/gi, 'Academia')
    .replace(/\bPanoramica\b/gi, 'Panorâmica')
    .replace(/\bArquitetonico\b/gi, 'Arquitetônico')
    .replace(/\bDependencia\b/gi, 'Dependência')
    .replace(/\bReuniao\b/gi, 'Reunião')
    .replace(/\bSuite\b/g, 'Suíte')
    .replace(/\bSuites\b/g, 'Suítes')
    .replace(/\bMercadinho 24H\b/gi, 'Mercadinho 24h')
    .replace(/\bSeguranca 24H\b/gi, 'Segurança 24h')
    .replace(/\bPortaria 24H\b/gi, 'Portaria 24h')
    .replace(/\b24H\b/gi, '24h');
}

// ============ Component ============
export default function AmenidadesPanelV2({ items = [], onRefresh, theme }) {
  const [renaming, setRenaming] = useState(null); // { oldName, newName }
  const [filter, setFilter] = useState('all'); // all | problemas | ok
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const stats = useMemo(() => {
    const usage = new Map(); // name -> { count, imovelIds: [] }
    for (const im of items) {
      if (!Array.isArray(im.amenidades)) continue;
      for (const a of im.amenidades) {
        if (!a) continue;
        const key = String(a);
        if (!usage.has(key)) usage.set(key, { count: 0, ids: [] });
        const u = usage.get(key);
        u.count++;
        u.ids.push(im.id);
      }
    }
    return Array.from(usage.entries())
      .map(([name, { count, ids }]) => ({ name, count, ids, problem: isProblematic(name) }))
      .sort((a, b) => {
        if (a.problem !== b.problem) return a.problem ? -1 : 1;
        if (b.count !== a.count) return b.count - a.count;
        return a.name.localeCompare(b.name, 'pt-BR');
      });
  }, [items]);

  const filtered = useMemo(() => {
    let list = stats;
    if (filter === 'problemas') list = list.filter(s => s.problem);
    if (filter === 'ok') list = list.filter(s => !s.problem);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q));
    }
    return list;
  }, [stats, filter, search]);

  const problemasCount = stats.filter(s => s.problem).length;

  // ===== Bulk operations =====
  const applyRename = async (oldName, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) { setMsg({ err: true, text: 'Nome novo não pode ser vazio' }); return; }
    if (trimmed === oldName) { setMsg({ err: true, text: 'O nome é igual ao atual' }); return; }
    const target = stats.find(s => s.name === oldName);
    if (!target) return;
    if (!window.confirm(`Renomear "${oldName}" → "${trimmed}" em ${target.count} imóvel${target.count > 1 ? 'is' : ''}?`)) return;
    setBusy(true); setMsg(null);
    try {
      // Atualiza cada imóvel. Se o novo nome já existe na lista, evita duplicar.
      for (const id of target.ids) {
        const imovel = items.find(i => i.id === id);
        if (!imovel) continue;
        const lista = Array.isArray(imovel.amenidades) ? imovel.amenidades.slice() : [];
        const novaLista = [];
        const seen = new Set();
        for (const a of lista) {
          const v = a === oldName ? trimmed : a;
          if (seen.has(v)) continue;
          seen.add(v);
          novaLista.push(v);
        }
        await supabase.from('mf_imoveis').update({ amenidades: novaLista }).eq('id', id);
      }
      setMsg({ ok: true, text: `Renomeado em ${target.count} imóvel${target.count > 1 ? 'is' : ''}.` });
      setRenaming(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setMsg({ err: true, text: 'Erro ao renomear: ' + e.message });
    } finally {
      setBusy(false);
    }
  };

  const applyDelete = async (name) => {
    const target = stats.find(s => s.name === name);
    if (!target) return;
    if (!window.confirm(`Remover "${name}" de ${target.count} imóvel${target.count > 1 ? 'is' : ''}? (não pode ser desfeito)`)) return;
    setBusy(true); setMsg(null);
    try {
      for (const id of target.ids) {
        const imovel = items.find(i => i.id === id);
        if (!imovel) continue;
        const lista = Array.isArray(imovel.amenidades) ? imovel.amenidades : [];
        const novaLista = lista.filter(a => a !== name);
        await supabase.from('mf_imoveis').update({ amenidades: novaLista }).eq('id', id);
      }
      setMsg({ ok: true, text: `Removido de ${target.count} imóvel${target.count > 1 ? 'is' : ''}.` });
      if (onRefresh) await onRefresh();
    } catch (e) {
      setMsg({ err: true, text: 'Erro ao remover: ' + e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header>
        <div>
          <h1>Amenidades</h1>
          <p>{stats.length} amenidade{stats.length !== 1 ? 's' : ''} cadastrada{stats.length !== 1 ? 's' : ''} em {items.length} imóv{items.length !== 1 ? 'eis' : 'el'}</p>
        </div>
      </Header>

      {msg && <Msg $ok={msg.ok} $err={msg.err}>{msg.text}</Msg>}

      <ToolBar>
        <input
          type="search"
          placeholder="Buscar amenidade…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Filter $active={filter === 'all'} onClick={() => setFilter('all')}>Todas · {stats.length}</Filter>
        <Filter $active={filter === 'problemas'} onClick={() => setFilter('problemas')}>
          ⚠ Com problema · {problemasCount}
        </Filter>
        <Filter $active={filter === 'ok'} onClick={() => setFilter('ok')}>Normalizadas · {stats.length - problemasCount}</Filter>
      </ToolBar>

      {filtered.length === 0 ? (
        <Empty>Nenhuma amenidade encontrada.</Empty>
      ) : (
        <ListBox>
          {filtered.map((s) => {
            const isRenaming = renaming && renaming.oldName === s.name;
            return (
              <React.Fragment key={s.name}>
                <Row $problem={s.problem} $mono={s.problem}>
                  <div className="name">
                    {s.problem && <span className="problem-flag">⚠ formatar</span>}
                    {s.name}
                  </div>
                  <div className="count">
                    {s.count}
                    <small style={{ display: 'block', fontFamily: 'Inter', fontSize: 9, color: theme?.muted, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 500 }}>
                      {s.count === 1 ? 'imóvel' : 'imóveis'}
                    </small>
                  </div>
                  <div className="actions">
                    {s.problem && (
                      <button
                        data-primary
                        onClick={() => setRenaming({ oldName: s.name, newName: suggestNormalized(s.name) })}
                        disabled={busy}
                      >
                        Normalizar
                      </button>
                    )}
                    <button
                      onClick={() => setRenaming({ oldName: s.name, newName: s.name })}
                      disabled={busy}
                    >
                      Renomear
                    </button>
                    <button data-danger onClick={() => applyDelete(s.name)} disabled={busy}>
                      Excluir
                    </button>
                  </div>
                </Row>
                {isRenaming && (
                  <RenameRow>
                    <input
                      autoFocus
                      value={renaming.newName}
                      onChange={(e) => setRenaming(r => ({ ...r, newName: e.target.value }))}
                      placeholder="Novo nome…"
                    />
                    <button onClick={() => setRenaming(null)} disabled={busy}>Cancelar</button>
                    <button data-primary onClick={() => applyRename(renaming.oldName, renaming.newName)} disabled={busy}>
                      {busy ? 'Aplicando…' : 'Aplicar'}
                    </button>
                    <div className="help">
                      Vai renomear "{renaming.oldName}" em todos os {s.count} imóveis. Use Capitalização correta (ex: "Segurança 24h").
                    </div>
                  </RenameRow>
                )}
              </React.Fragment>
            );
          })}
        </ListBox>
      )}
    </>
  );
}
